import { formatPhoneForDisplay, LIMITS, threadIdFor } from '@crew/shared';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAdmin } from '@/auth/AuthProvider';
import { EditPersonDialog } from '@/components/contractors/EditPersonForm';
import { BackIcon, ChatIcon, ChevronNextIcon, ContractorsIcon, EditIcon, PhoneIcon, PlusIcon, WhatsAppIcon } from '@/components/icons';
import { TypeChip } from '@/components/jobs/RegistrationsPanel';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { Notice } from '@/components/ui/Notice';
import { NumberStepper } from '@/components/ui/NumberStepper';
import { PageHeader } from '@/components/ui/PageHeader';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import { useActiveJobs } from '@/hooks/useActiveJobs';
import { useContractor, useContractorHistory, useContractors, type ContractorView } from '@/hooks/useAdminData';
import { formatJobDate } from '@/lib/format';
import { adminApi } from '@/services/adminApi';
import { errorMessage } from '@/services/apiError';

type Filter = 'all' | 'contractor' | 'worker';

/** מאגר הקבלנים והעובדים: כל מי שנרשם אי פעם, לפי טלפון. */
export function ContractorsPage() {
  const list = useContractors();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const shown = useMemo(() => {
    if (list.state !== 'ready') return [];
    const term = search.trim();
    const digits = term.replace(/\D/g, '');
    return list.data.filter(
      (c) =>
        (filter === 'all' || c.lastRegistrantType === filter) &&
        (!term || c.fullName.includes(term) || (digits.length >= 3 && `0${c.phone.slice(4)}`.includes(digits))),
    );
  }, [list, search, filter]);

  const counts = list.state === 'ready' ? { contractor: list.data.filter((c) => c.lastRegistrantType === 'contractor').length, worker: list.data.filter((c) => c.lastRegistrantType === 'worker').length } : null;

  return (
    <>
      <PageHeader title="מאגר קבלנים ועובדים" subtitle={list.state === 'ready' ? `${list.data.length} רשומות` : undefined} />

      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="חיפוש לפי שם או טלפון"
          aria-label="חיפוש במאגר"
          className="block min-h-12 w-full rounded-control border border-line-strong bg-surface px-3.5 text-[16px] placeholder:text-ink-3 focus:border-brand focus:outline-none md:max-w-sm"
        />
        <div role="tablist" className="inline-flex self-start rounded-control bg-sunken p-1">
          {(
            [
              ['all', 'הכל'],
              ['contractor', `קבלנים${counts ? ` (${counts.contractor})` : ''}`],
              ['worker', `עובדים${counts ? ` (${counts.worker})` : ''}`],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={filter === k}
              onClick={() => setFilter(k)}
              className={`min-h-10 rounded-[8px] px-3 text-[14px] font-medium ${filter === k ? 'bg-surface text-ink shadow-sm' : 'text-ink-2'}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {list.state === 'loading' && (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      )}
      {list.state === 'error' && <Notice tone="error">{list.message}</Notice>}
      {list.state === 'ready' && list.data.length === 0 && (
        <EmptyState icon={<ContractorsIcon size={30} />} title="המאגר ריק" body="כל קבלן או עובד שנרשם לעבודה נשמר כאן אוטומטית לפי מספר הטלפון." />
      )}
      {list.state === 'ready' && list.data.length > 0 && shown.length === 0 && <p className="py-8 text-center text-ink-2">לא נמצאו התאמות</p>}

      {shown.length > 0 && (
        <ul className="divide-y divide-line overflow-hidden rounded-card border border-line bg-surface">
          {shown.map((c) => (
            <li key={c.id}>
              <Link to={`/contractors/${c.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-sunken/60 active:bg-sunken">
                <TypeChip type={c.lastRegistrantType ?? 'contractor'} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[15px] font-semibold">{c.fullName}</div>
                  <div className="ltr text-start text-[13px] text-ink-2">{formatPhoneForDisplay(c.phone)}</div>
                </div>
                <span className="shrink-0 text-[13px] text-ink-3">{c.jobCount} עבודות</span>
                <ChevronNextIcon size={18} className="shrink-0 text-ink-3" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export function ContractorDetailPage() {
  const { contractorId = '' } = useParams();
  const c = useContractor(contractorId);
  const history = useContractorHistory(c.state === 'ready' ? c.data : null);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);

  if (c.state === 'loading') {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }
  if (c.state === 'error') return <Notice tone="error">{c.message}</Notice>;
  if (!c.data) {
    return (
      <div className="py-12 text-center">
        <h1 className="text-[20px] font-bold">הרשומה לא נמצאה</h1>
        <Link to="/contractors" className="mt-4 inline-block font-semibold text-brand">
          למאגר
        </Link>
      </div>
    );
  }
  const person = c.data;
  const total = history.state === 'ready' ? history.data.reduce((n, h) => n + h.reg.workerCount, 0) : null;

  return (
    <div className="mx-auto max-w-3xl">
      <Link to="/contractors" className="mb-3 inline-flex items-center gap-1.5 text-[14px] font-medium text-ink-2 hover:text-ink">
        <BackIcon size={17} />
        מאגר
      </Link>

      <section className="rounded-card border border-line bg-surface p-4 md:p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <TypeChip type={person.lastRegistrantType ?? 'contractor'} />
            <h1 className="mt-2 text-[24px] font-extrabold leading-tight">{person.fullName}</h1>
            <p className="ltr mt-1 text-start text-[15px] text-ink-2">{formatPhoneForDisplay(person.phone)}</p>
          </div>
          <Button variant="secondary" className="min-h-10 px-3 text-[14px]" icon={<EditIcon size={16} />} onClick={() => setEditing(true)}>
            עריכה
          </Button>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2 sm:flex">
          <a href={`tel:${person.phone}`} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-control border border-line-strong px-4 text-[14px] font-semibold">
            <PhoneIcon size={17} />
            חיוג
          </a>
          <a
            href={`https://wa.me/${person.phone.replace(/^\+/, '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-control border border-line-strong px-4 text-[14px] font-semibold"
          >
            <WhatsAppIcon size={17} />
            וואטסאפ
          </a>
          <Button className="col-span-2 min-h-11 text-[14px]" icon={<PlusIcon size={17} />} onClick={() => setAdding(true)}>
            הוספה לעבודה
          </Button>
        </div>
      </section>

      <section className="mt-5 rounded-card border border-line bg-surface p-4 md:p-5">
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="text-[17px] font-bold">היסטוריית עבודות</h2>
          {total !== null && (
            <span className="text-[13px] text-ink-3">
              {person.jobCount} עבודות · {total} עובדים בסה״כ
            </span>
          )}
        </div>
        {history.state === 'loading' && (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        )}
        {history.state === 'error' && <Notice tone="error">{history.message}</Notice>}
        {history.state === 'ready' && history.data.length === 0 && <p className="text-[14px] text-ink-3">אין עבודות פעילות או שמורות.</p>}
        {history.state === 'ready' && history.data.length > 0 && (
          <ul className="divide-y divide-line">
            {history.data.map(({ job, reg }) => (
              <li key={job.id} className="flex items-center gap-2">
                <Link to={`/jobs/${job.id}`} className="flex min-w-0 flex-1 items-center gap-3 py-2.5 hover:text-brand">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[15px] font-semibold">{job.name}</div>
                    <div className="text-[13px] text-ink-2">
                      {formatJobDate(job.date)}
                      {job.status === 'archived' ? ' · בארכיון' : ''}
                    </div>
                  </div>
                  <TypeChip type={reg.registrantType} />
                  <span className="tabular min-w-10 rounded-[6px] bg-tag px-2 py-1 text-center text-[15px] font-bold text-tag-ink shadow-[inset_0_-3px_0_var(--color-tag-bar)]">
                    {reg.workerCount}
                  </span>
                </Link>
                <Link
                  to={`/messages/${threadIdFor(job.id, person.id)}`}
                  aria-label={`שיחה עם ${person.fullName} — ${job.name}`}
                  title="שיחה בעבודה הזו"
                  className="flex size-10 shrink-0 items-center justify-center rounded-full text-brand hover:bg-sunken"
                >
                  <ChatIcon size={19} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <EditPersonDialog open={editing} person={person} onClose={() => setEditing(false)} />
      <AddToJobDialog open={adding} person={person} history={history.state === 'ready' ? history.data.map((h) => h.job.id) : []} onClose={() => setAdding(false)} />
    </div>
  );
}

function AddToJobDialog({ open, person, history, onClose }: { open: boolean; person: ContractorView; history: string[]; onClose: () => void }) {
  const { user } = useAdmin();
  const toast = useToast();
  const jobs = useActiveJobs();
  const [jobId, setJobId] = useState('');
  const [type, setType] = useState(person.lastRegistrantType ?? 'contractor');
  const [count, setCount] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const available = jobs.state === 'ready' ? jobs.data.filter((j) => !history.includes(j.id)) : [];

  const close = () => {
    setLink(null);
    setJobId('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} title={link ? 'נוסף לעבודה' : `הוספת ${person.fullName} לעבודה`}>
      {link ? (
        <>
          <p>שלחו את הקישור האישי — דרכו אפשר לבקש שינוי או ביטול.</p>
          <p className="ltr mt-3 break-all rounded-control bg-sunken p-3 text-start text-[13px] text-ink">{link}</p>
          <a
            href={`https://wa.me/${person.phone.replace(/^\+/, '')}?text=${encodeURIComponent(link)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 flex min-h-12 items-center justify-center gap-2 rounded-control bg-[#1f7a4d] text-[15px] font-semibold text-white"
          >
            <WhatsAppIcon size={18} />
            שליחה בוואטסאפ
          </a>
          <Button variant="secondary" block className="mt-2" onClick={close}>
            סגירה
          </Button>
        </>
      ) : (
        <div className="space-y-4">
          {error && <Notice tone="error">{error}</Notice>}
          <label className="block">
            <span className="mb-1.5 block text-[14px] font-medium">עבודה</span>
            <select
              value={jobId}
              onChange={(e) => setJobId(e.target.value)}
              className="block min-h-12 w-full rounded-control border border-line-strong bg-surface px-3 text-[16px] text-ink focus:border-brand focus:outline-none"
            >
              <option value="">בחירת עבודה…</option>
              {available.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.name} — {formatJobDate(j.date)}
                </option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-2">
            {(['contractor', 'worker'] as const).map((t) => (
              <button
                key={t}
                type="button"
                aria-pressed={type === t}
                onClick={() => setType(t)}
                className={`min-h-11 rounded-control border-2 text-[15px] font-semibold ${type === t ? 'border-brand bg-brand-soft text-ink' : 'border-line-strong'}`}
              >
                {t === 'contractor' ? 'קבלן' : 'עובד עצמאי'}
              </button>
            ))}
          </div>
          {type === 'contractor' && <NumberStepper label="כמות עובדים" value={count} onChange={setCount} min={1} max={LIMITS.maxWorkers} />}
          <Button
            block
            loading={busy}
            disabled={!jobId || (type === 'contractor' && (count === '' || count < 1))}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                const res = await adminApi(user).addRegistration(jobId, {
                  contractorId: person.id,
                  registrantType: type,
                  ...(type === 'contractor' ? { workerCount: count as number } : {}),
                });
                toast('נוסף לעבודה');
                setLink(`${window.location.origin}/c/${res.personalToken}`);
              } catch (err) {
                setError(errorMessage(err));
              } finally {
                setBusy(false);
              }
            }}
          >
            הוספה
          </Button>
        </div>
      )}
    </Dialog>
  );
}
